import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const DelaminationPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    breadcrumb: { home: 'ホーム', troubleshooting: 'トラブル対策', title: '層間剥離' },
    badge: '重要',
    title: '層間剥離',
    description: '積層した層同士の接着が不十分で、層が剥がれる現象。強度に直結する重大なトラブル。',
    symptoms: {
      title: '症状の例',
      items: [
        '層と層の間に隙間が見える',
        '造形物が簡単に層ごとに割れる',
        '側面に横方向の亀裂が入る',
        '造形途中で層が分離してしまう'
      ]
    },
    causes: {
      title: '主な原因',
      items: [
        { title: '1. ノズル温度不足', desc: '温度が低すぎると、新しい層が前の層に十分に溶着せず、層間接着が弱くなります。' },
        { title: '2. 冷却過多', desc: 'パートクーリングファンが強すぎると、前の層が冷えすぎて新しい層との接着が弱くなります。' },
        { title: '3. 層高が高すぎる', desc: '層高が高いと、層同士の接触面積が減り、接着力が低下します。' },
        { title: '4. 印刷速度過速', desc: '速度が速すぎると、樹脂が十分に溶着する時間がなく、層間接着が弱くなります。' }
      ]
    },
    solutions: {
      title: '対策・チェックポイント',
      items: [
        {
          title: 'ノズル温度の上昇',
          desc: '推奨温度範囲の上限に近づけます。',
          points: ['5°C刻みで上げてテスト', '糸引きが増えない範囲で調整', '温度タワーで最適温度を見つける']
        },
        {
          title: 'ファン速度の調整',
          desc: '冷却を弱めて層間温度を保ちます。',
          points: ['ファン速度を10-20%下げる', '小さなパーツでは最小レイヤー時間を設定', 'ABS/ASAではファンをほぼ使わない']
        },
        {
          title: '層高の調整',
          desc: '層高を下げることで接着面積を増やします。',
          points: ['0.4mmノズルなら0.2mm以下を推奨', '強度重視なら0.12-0.16mmも検討', 'ノズル径の50-75%が目安']
        },
        {
          title: '印刷速度の低減',
          desc: '速度を落として層間接着時間を確保します。',
          points: ['外壁速度: 30-50mm/s', 'インフィル速度: 50-80mm/s', '強度重視の場合はさらに遅く']
        },
        {
          title: 'フロー率の調整',
          desc: 'フロー率を上げて層間の接触を改善します。',
          points: ['基本は100%から開始', '層間剥離がある場合は102-105%に上げる', '上げすぎると寸法精度が悪化']
        },
        {
          title: 'エンクロージャーの使用',
          desc: '庫内温度を保つことで層間接着を改善します。',
          points: ['ABS/ASA/PCでは特に有効', '庫内温度40-60°Cを維持', 'PLAでは不要（むしろ悪影響の場合も）']
        }
      ]
    },
    related: {
      title: '関連情報',
      items: [
        { title: 'ABS フィラメント', desc: '層間剥離が発生しやすい材料', link: '/filament/abs' },
        { title: '反り（ワーピング）', desc: '関連するトラブル', link: '/troubleshooting/warping' }
      ]
    },
    backLink: 'トラブル対策一覧に戻る'
  };

  const TEXT_EN = {
    breadcrumb: { home: 'Home', troubleshooting: 'Troubleshooting', title: 'Layer Delamination' },
    badge: 'Important',
    title: 'Layer Delamination',
    description: 'Insufficient adhesion between layers causing separation. Critical issue directly affecting strength.',
    symptoms: {
      title: 'Symptoms',
      items: [
        'Visible gaps between layers',
        'Print easily splits along layer lines',
        'Horizontal cracks on sides',
        'Layers separating during printing'
      ]
    },
    causes: {
      title: 'Main Causes',
      items: [
        { title: '1. Insufficient Nozzle Temperature', desc: 'Too low temperature prevents new layers from properly fusing to previous layers, weakening layer adhesion.' },
        { title: '2. Excessive Cooling', desc: 'Too much part cooling causes previous layers to cool excessively, weakening adhesion with new layers.' },
        { title: '3. Layer Height Too High', desc: 'High layer height reduces contact area between layers, decreasing adhesion strength.' },
        { title: '4. Print Speed Too Fast', desc: 'Excessive speed doesn\'t allow enough time for proper layer fusion, weakening adhesion.' }
      ]
    },
    solutions: {
      title: 'Solutions & Checkpoints',
      items: [
        {
          title: 'Increase Nozzle Temperature',
          desc: 'Approach the upper end of recommended temperature range.',
          points: ['Increase in 5°C increments and test', 'Adjust within range that doesn\'t increase stringing', 'Use temperature tower to find optimal temperature']
        },
        {
          title: 'Adjust Fan Speed',
          desc: 'Reduce cooling to maintain layer temperature.',
          points: ['Decrease fan speed by 10-20%', 'Set minimum layer time for small parts', 'Use minimal fan for ABS/ASA']
        },
        {
          title: 'Adjust Layer Height',
          desc: 'Lower layer height increases adhesion area.',
          points: ['0.2mm or less recommended for 0.4mm nozzle', 'Consider 0.12-0.16mm for strength priority', '50-75% of nozzle diameter is guideline']
        },
        {
          title: 'Reduce Print Speed',
          desc: 'Slower speed ensures layer adhesion time.',
          points: ['Wall speed: 30-50mm/s', 'Infill speed: 50-80mm/s', 'Even slower for strength priority']
        },
        {
          title: 'Adjust Flow Rate',
          desc: 'Increase flow rate to improve layer contact.',
          points: ['Start from 100% baseline', 'Increase to 102-105% if delamination occurs', 'Too high reduces dimensional accuracy']
        },
        {
          title: 'Use Enclosure',
          desc: 'Maintaining chamber temperature improves layer adhesion.',
          points: ['Especially effective for ABS/ASA/PC', 'Maintain chamber temperature 40-60°C', 'Not needed for PLA (may be detrimental)']
        }
      ]
    },
    related: {
      title: 'Related Information',
      items: [
        { title: 'ABS Filament', desc: 'Material prone to delamination', link: '/filament/abs' },
        { title: 'Warping', desc: 'Related issue', link: '/troubleshooting/warping' }
      ]
    },
    backLink: 'Back to Troubleshooting List'
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const prefix = isEnglish ? '/en' : '';

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: `${prefix}/` },
    { label: TEXT.breadcrumb.troubleshooting, path: `${prefix}/troubleshooting` },
    { label: TEXT.breadcrumb.title }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span className="tech-label bg-[#b56b3c] text-white">{TEXT.badge}</span>
                <h1 className="text-4xl font-bold text-white font-['Exo']">
                  {TEXT.title}
                </h1>
              </div>
              <p className="text-metal-light text-lg">
                {TEXT.description}
              </p>
              <div className="section-divider"></div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.symptoms.title}
              </h2>
              <ul className="space-y-3 text-metal-light">
                {TEXT.symptoms.items.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <i className="ri-arrow-right-s-line text-accent mt-1"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.causes.title}
              </h2>
              <div className="space-y-4">
                {TEXT.causes.items.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-l-[#b56b3c] pl-4">
                    <h3 className="text-lg font-semibold text-accent font-['Exo'] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.solutions.title}
              </h2>
              <div className="space-y-4">
                {TEXT.solutions.items.map((item, idx) => (
                  <div key={idx} className="clickable-card p-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-[#5f8d63] text-xl mt-1"></i>
                      <div>
                        <h3 className="text-lg font-semibold text-white font-['Exo'] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-metal-light text-sm mb-2">
                          {item.desc}
                        </p>
                        <ul className="text-metal-light text-sm space-y-1 ml-4">
                          {item.points.map((point, pidx) => (
                            <li key={pidx}>• {point}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.related.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TEXT.related.items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={`${prefix}${item.link}`}
                    className="clickable-card p-4 hover:border-l-4 hover:border-l-accent transition-all"
                  >
                    <h3 className="text-white font-['Exo'] font-semibold mb-1">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to={`${prefix}/troubleshooting`}
                className="flex items-center space-x-2 text-accent hover:text-white transition-colors font-['Roboto_Mono']"
              >
                <i className="ri-arrow-left-line"></i>
                <span>{TEXT.backLink}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DelaminationPage;
